import type {
  BindPersona,
  BindProgressDto,
  BindSubjectivityDto,
} from './types';

export const EFFECTIVE_DATE = 'effective-date';
export const COMMISSION = 'commission';
export const UPLOAD_BIND_ORDER = 'upload-bind-order';
export const BIND = 'bind';
export const ISSUE = 'issue';

export type BindStepKey =
  | typeof EFFECTIVE_DATE
  | typeof COMMISSION
  | typeof UPLOAD_BIND_ORDER
  | typeof BIND
  | typeof ISSUE
  | `pre-bind/${string}`
  | `pre-issuance/${string}`;

export const BIND_LOCKS = {
  bindOrderFirst: 'Upload the bind order first',
  versionBlocked: 'Choose an effective date that can be bound first',
  preBindFirst: 'Complete the pre-bind requirements first',
  boundFirst: 'Available once the quote is bound',
  issueFirst:
    'Available once the quote is bound and the pre-issuance requirements are complete',
  referred: 'Referred to an underwriter',
} as const;

export interface JourneyItem {
  readonly key: BindStepKey;
  readonly label: string;
  readonly done: boolean;
  readonly lock: string | null;
  /** Why the step is worth a second look though it opens, as a referral: shown by the navigator, never a lock. */
  readonly note: string | null;
  readonly editable: boolean;
  readonly subjectivity: BindSubjectivityDto | null;
}

export interface JourneyGroup {
  readonly label: string;
  readonly items: readonly JourneyItem[];
}

export interface JourneyInput {
  readonly progress: BindProgressDto;
  readonly persona: BindPersona;
  readonly productType: string;
  readonly bindOrderUploaded: boolean;
  readonly renewalOrMigration: boolean;
  readonly versionTransitionBlocked: boolean;
}

export interface BindJourney {
  readonly status: string;
  readonly groups: readonly JourneyGroup[];
  readonly order: readonly BindStepKey[];
  readonly landing: BindStepKey;
  readonly item: (key: string) => JourneyItem | null;
}

/** The quote statuses the bind reads, as the server names them. */
export const READY = 'READY';
export const INDICATION = 'INDICATION';
export const REFERRED = 'REFERRED';
export const IN_REVIEW = 'IN_REVIEW';
export const INVALID = 'INVALID';
export const BOUND = 'BOUND';
export const SIGNED = 'SIGNED';
export const ISSUED = 'ISSUED';
export const IN_FORCE = 'IN FORCE';

/** The product types the bind treats differently. */
export const P100 = 'P100';
export const P100_PRO = 'P100_PRO';
export const P250 = 'P250';
export const ZSP = 'ZSP';

const PRE_BIND = 'PreBind';
const COMMISSION_PRODUCTS = ['P_ONE', 'PRIME_ONE_AU'];

/** Statuses once the policy is signed, issued or in force. */
export function isIssuedOrLater(status: string): boolean {
  return status === SIGNED || status === ISSUED || status === IN_FORCE;
}

/** Statuses from the moment the quote is bound onward. */
export const isPostBind = (status: string): boolean =>
  status === BOUND || isIssuedOrLater(status);

export const isManuscript = (key: string): boolean => key.includes('M');

const compareKeys = (a: string, b: string): number => {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
};

/** Ascending by key, with manuscript keys after the rest. */
export function sortSubjectivityKeys(keys: readonly string[]): string[] {
  const rest = keys.filter((key) => !isManuscript(key)).sort(compareKeys);
  const manuscript = keys.filter(isManuscript).sort(compareKeys);
  return [...rest, ...manuscript];
}

export function isStepEditable({
  subjectivitySubType,
  status,
  isAPI,
  productType,
}: Readonly<{
  subjectivitySubType: string;
  status: string;
  isAPI: boolean;
  productType: string;
}>): boolean {
  if (isAPI && productType === P250) {
    return true;
  }
  // Legacy's rule, which the legacy stepper reads through this function: a pre-bind step closes on BOUND and SIGNED
  // only, and stays open on an ISSUED or IN FORCE quote. The journey's locks use isPostBind instead.
  if (subjectivitySubType === PRE_BIND) {
    return status !== BOUND && status !== SIGNED;
  }
  return !isIssuedOrLater(status);
}

const sortedByKey = (
  subjectivities: readonly BindSubjectivityDto[]
): BindSubjectivityDto[] =>
  [...subjectivities].sort((a, b) => {
    const manuscript =
      Number(isManuscript(a.subjectivityKey)) -
      Number(isManuscript(b.subjectivityKey));
    return manuscript || compareKeys(a.subjectivityKey, b.subjectivityKey);
  });

export function bindJourney(input: JourneyInput): BindJourney {
  const {
    progress,
    persona,
    productType,
    bindOrderUploaded,
    renewalOrMigration,
    versionTransitionBlocked,
  } = input;
  const status = progress.quoteAgencyStatus;
  const isAPI = progress.isAPI ?? false;
  const isCowbell = persona === 'COWBELL';
  const isBound = isPostBind(status);
  const isSigned = isIssuedOrLater(status);
  const bindDone = isBound;

  const preBind = sortedByKey(progress.preBindSubjectivities ?? []);
  const preIssuance = sortedByKey(progress.preIssuanceSubjectivities ?? []);
  const preBindOpen = preBind.some((s) => !s.completed);
  const preIssuanceOpen = preIssuance.some((s) => !s.completed);

  const lockFor = (key: BindStepKey): string | null => {
    if (key === UPLOAD_BIND_ORDER) {
      return null;
    }
    if (isCowbell && !bindOrderUploaded) {
      return BIND_LOCKS.bindOrderFirst;
    }
    if (key !== EFFECTIVE_DATE && versionTransitionBlocked) {
      return BIND_LOCKS.versionBlocked;
    }
    if (key === BIND && preBindOpen) {
      return BIND_LOCKS.preBindFirst;
    }
    if (key.startsWith('pre-issuance/') && productType === P100 && !isBound) {
      return BIND_LOCKS.boundFirst;
    }
    if (
      key === ISSUE &&
      !isSigned &&
      (!isBound || preBindOpen || preIssuanceOpen)
    ) {
      return BIND_LOCKS.issueFirst;
    }
    return null;
  };

  const noteFor = (key: BindStepKey): string | null => {
    const referred = status === REFERRED || status === IN_REVIEW;
    return key === BIND && referred ? BIND_LOCKS.referred : null;
  };

  const make = (
    key: BindStepKey,
    label: string,
    done: boolean,
    editable: boolean,
    subjectivity: BindSubjectivityDto | null = null
  ): JourneyItem => ({
    key,
    label,
    done,
    lock: lockFor(key),
    note: noteFor(key),
    editable,
    subjectivity,
  });

  const subjectivityItem = (
    prefix: 'pre-bind' | 'pre-issuance',
    s: BindSubjectivityDto
  ): JourneyItem =>
    make(
      `${prefix}/${s.subjectivityKey}`,
      s.subjectivityTitle,
      s.completed,
      isStepEditable({
        subjectivitySubType: s.subjectivitySubType,
        status,
        isAPI,
        productType,
      }),
      s
    );

  const effectiveDateEditable =
    !bindDone && !renewalOrMigration && productType !== P100_PRO;

  const groups: JourneyGroup[] = [];
  if (isCowbell) {
    groups.push({
      label: 'Bind order',
      items: [
        make(UPLOAD_BIND_ORDER, 'Upload bind order', bindOrderUploaded, true),
      ],
    });
  }

  const dateItems = [
    make(EFFECTIVE_DATE, 'Policy effective date', true, effectiveDateEditable),
  ];
  if (!isCowbell && COMMISSION_PRODUCTS.includes(productType)) {
    dateItems.push(make(COMMISSION, 'Commission percentage', true, !bindDone));
  }
  groups.push({ label: 'Effective date', items: dateItems });

  if (preBind.length > 0) {
    groups.push({
      label: 'Pre-bind requirements',
      items: preBind.map((s) => subjectivityItem('pre-bind', s)),
    });
  }

  const bindLabel = productType === P100 ? 'Request to bind' : 'Bind quote';
  groups.push({
    label: bindLabel,
    items: [make(BIND, bindLabel, bindDone, !bindDone)],
  });

  if (preIssuance.length > 0) {
    groups.push({
      label: 'Pre-issuance requirements',
      items: preIssuance.map((s) => subjectivityItem('pre-issuance', s)),
    });
  }

  groups.push({
    label: 'Issue policy',
    items: [make(ISSUE, 'Issue policy', isSigned, !isSigned)],
  });

  const items = groups.flatMap((group) => group.items);

  // Where the broker is in the bind: the first step still to do that opens, else the last that opens.
  const landingOf = (): BindStepKey => {
    if (isCowbell && !bindOrderUploaded) {
      return UPLOAD_BIND_ORDER;
    }
    if (isSigned) {
      return ISSUE;
    }
    const open = items.filter((i) => i.lock === null);
    const next = open.find((i) => !i.done);
    return (next ?? open[open.length - 1] ?? items[0]).key;
  };

  return {
    status,
    groups,
    order: items.map((i) => i.key),
    landing: landingOf(),
    item: (key) => items.find((i) => i.key === key) ?? null,
  };
}

/** The next unlocked step after `key`, or the landing step when none is left. */
export function stepAfter(journey: BindJourney, key: BindStepKey): BindStepKey {
  const at = journey.order.indexOf(key);
  if (at < 0) {
    return journey.landing;
  }
  const next = journey.order
    .slice(at + 1)
    .find((candidate) => journey.item(candidate)?.lock === null);
  return next ?? journey.landing;
}
